import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useIntersection } from 'react-use'

import Button from '@/components/Button'
import ColorCard from '@/components/ColorCard'
import Heading from '@/components/Heading'
import Link from '@/components/Link'

import DefaultComponentProps from '@/types/DefaultComponentProps'

import styles from './Section.module.scss'
import { routes } from '@/config'

type CpaProps = {
  heading: string
  summary: string
  blocks: { [key: string]: { [key: string]: string } }
}

const BLOCKS = {
  audience: {
    image: 'pic9.png'
  },
  actions: {
    image: 'pic10.png'
  }
}

export const Cpa: FC<DefaultComponentProps> = ({ className = '' }) => {
  const { t } = useTranslation()

  const { heading, summary, blocks } = t('cpa:data', {
    returnObjects: true
  }) as CpaProps

  const [animationStart, setAnimationStart] = useState(false)

  const intersectionRef = useRef(null)

  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.4
  })

  useEffect(() => {
    if (intersection?.isIntersecting) {
      setAnimationStart(true)
    }
  }, [intersection?.isIntersecting])

  return (
    <section
      ref={intersectionRef}
      className={clsx(styles['section'], className, {
        [styles['section_animation-start']]: animationStart
      })}
    >
      <div className={styles['section__content']}>
        <div className={styles['section__col']}>
          <Heading className={styles['section__heading']} level={2}>
            {heading}
          </Heading>
          <div
            className={styles['section__summary']}
            dangerouslySetInnerHTML={{ __html: summary }}
          />
          <div className={styles['section__buttons']}>
            <Button
              title={t('forms:buttons.signup.label') as string}
              href={routes.REGISTER}
              as={Link}
              size={'l'}
              className={styles['section__button-link']}
            >
              {t('forms:buttons.learnMore.label')}
            </Button>
            <Button
              title={t('forms:buttons.signin.label') as string}
              href={routes.LOGIN}
              as={Link}
              className={styles['section__button-link']}
              size={'l'}
              variant={'ghost'}
            >
              {t('forms:buttons.fillOutTheBrief.label')}
            </Button>
          </div>
        </div>
        <div className={styles['section__col']}>
          <ul className={styles['section__blocks']}>
            {Object.entries(BLOCKS).map(([key, { image }]) => (
              <li key={key} className={styles['section__blocks-item']}>
                <ColorCard
                  {...blocks[key]}
                  id={key}
                  image={image}
                  className={styles['section__blocks-color-card']}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
